/**
 * Routes a store is allowed to sit on while it is still installing.
 *
 * `App.vue` pushes any store whose `passed_installing_flow === 'new'` to
 * `/onboard-pricing` from every route NOT on this list. That is right for
 * ordinary pages — an unsubscribed merchant should be choosing a plan — and
 * fatal on the billing callback path: a merchant returning from Shopify with a
 * `?charge_id` gets bounced to pricing and the charge id is lost, with no way
 * to recover. That is the loop `SubscriptionConfirm.vue` warns about.
 *
 * Lives here rather than as a property on `App.vue` so it can be tested
 * without mounting the entire application (AS-10884).
 *
 * ⚠️ **Adding an atlas route here is a product decision, not a formality.**
 * Exempting the catch-all `atlasAgent` (`/assistant/:agent/:tab*`) would
 * exempt the WHOLE atlas surface, letting a merchant who has never picked a
 * plan roam the app. Only add the specific atlas route a classic installation
 * route redirects to — and give that route its own name in the router rather
 * than letting it fall through to the catch-all.
 *
 * There is none today: the billing surface is classic-only, and its old
 * /assistant/* paths are router redirects that never become `$route`.
 */
export const INSTALLATION_ROUTE_NAMES: string[] = [
  'onboard',
  'onboardContact',
  'onboardMessaging',
  'onboardReport',
  'onboardPurpose',
  'onboardComplete',
  'onboardPricing',
  'subscriptionConfirm',
  'autoSubscription',
  'dataImporting',
  'StockySwitcher',
  'StockyDataMigration',
];

/**
 * True when a store still in the installation flow may stay on this route.
 *
 * `App.vue` reads `this.$route.name || ''`, so an unnamed route arrives as an
 * empty string; that must take the safe branch (bounce to onboarding) rather
 * than accidentally skipping it.
 */
export function isInstallationRoute(routeName: string | null | undefined): boolean {
  if (!routeName) return false;
  return INSTALLATION_ROUTE_NAMES.includes(routeName);
}

/**
 * Routes a store that has FINISHED installing may **not** sit on.
 *
 * The mirror image of the list above, and a genuinely different list — `App.vue`
 * bounces a store whose `passed_installing_flow !== 'new'` off these back to
 * home (or to Shopify Admin when standalone), unless `?preview=1` in non-prod.
 *
 * Note it is deliberately **not** a copy of `INSTALLATION_ROUTE_NAMES`: it omits
 * `onboardMessaging` and `dataImporting`, which an onboarded store is allowed to
 * revisit. Tidying the two into one list would change behaviour.
 *
 * Extracted from an inline array literal in `App.vue` (AS-10886) for the same
 * reason the first list was: so it can be tested without mounting the app. The
 * two now sit side by side, which is the point — migrating an onboarding page to
 * atlas needs the atlas route name on BOTH, and missing either is a real bug in
 * opposite directions. (/onboard-pricing was migrated once and taken back to
 * classic, which is why neither list carries an atlas name now.)
 */
export const ONBOARD_EXIT_ROUTE_NAMES: string[] = [
  'onboard',
  'onboardContact',
  'onboardReport',
  'onboardPurpose',
  'onboardComplete',
  'onboardPricing',
];

/**
 * True when a store past the installation flow must be bounced off this route.
 *
 * Same empty-string caution as `isInstallationRoute`: `App.vue` reads
 * `this.$route.name || ''`, and an unnamed route must take the safe branch —
 * here that means *not* bouncing a merchant off a page they are legitimately on.
 */
export function isOnboardExitRoute(routeName: string | null | undefined): boolean {
  if (!routeName) return false;
  return ONBOARD_EXIT_ROUTE_NAMES.includes(routeName);
}
